import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { getFurnitureTypes, createProductVariant, updateProductVariant, logError } from '../api';
import FavoriteDropdown from './Global/FavoriteDropdown';

const ProductVariantModal = ({ show, onHide, onSave, variant = null }) => {
    const [formData, setFormData] = useState({
        typeId: '',
        name: '',
        priceDelta: 0
    });
    const [furnitureTypes, setFurnitureTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (show) {
            loadFurnitureTypes();
            setErrorMessage('');
            if (variant) {
                setFormData({
                    typeId: variant.typeId || '',
                    name: variant.name || '',
                    priceDelta: variant.priceDelta || 0
                });
            } else {
                setFormData({ typeId: '', name: '', priceDelta: 0 });
            }
        }
    }, [show, variant]);

    const loadFurnitureTypes = async () => {
        try {
            const types = await getFurnitureTypes();
            setFurnitureTypes(types);
        } catch (error) {
            logError(error);
            console.error('Error loading furniture types:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');
        
        try {
            const data = {
                ...formData,
                typeId: Number(formData.typeId),
                priceDelta: Number(formData.priceDelta)
            };

            if (variant) {
                await updateProductVariant(variant.id, data);
            } else {
                await createProductVariant(data);
            }
            
            onSave();
            onHide();
        } catch (error) {
            logError(error);
            console.error('Error saving product variant:', error);
            
            if (error.response?.status === 409) {
                const errorData = error.response.data;
                if (errorData && errorData.message) {
                    setErrorMessage(errorData.message);
                } else {
                    setErrorMessage('Ya existe una variante con este nombre en este tipo');
                }
            } else {
                setErrorMessage('Error al guardar la variante');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errorMessage) {
            setErrorMessage('');
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>{variant ? 'Editar' : 'Nueva'} Variante de Producto</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {errorMessage && (
                        <div className="alert alert-danger" role="alert" data-testid="variant-error-message">
                            {errorMessage}
                        </div>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo</Form.Label>
                        <FavoriteDropdown
                            name="typeId"
                            value={formData.typeId}
                            onChange={handleChange}
                            options={furnitureTypes.map(type => ({
                                id: type.id,
                                name: type.typeName,
                                isFavorite: type.isFavorite
                            }))}
                            placeholder="Seleccionar tipo"
                            required
                            data-testid="variant-type-select"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Nombre</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            data-testid="variant-name-input"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Precio añadido (€)</Form.Label>
                        <Form.Control
                            type="number"
                            step="1"
                            name="priceDelta"
                            value={formData.priceDelta}
                            onChange={handleChange}
                            required
                            data-testid="variant-price-input"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} data-testid="variant-cancel-button">
                        Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading} data-testid="variant-submit-button">
                        {loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ProductVariantModal;